import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  Input,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IconComponent } from '../icon/icon';
import type { SelectOption } from '../select/select';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ui-multiselect',
  imports: [IconComponent],
  styleUrl: './multiselect.scss',
  templateUrl: './multiselect.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiselectComponent),
      multi: true,
    },
  ],
})
export class MultiselectComponent implements ControlValueAccessor {
  /** Libellé affiché au-dessus du champ. */
  @Input() label = '';

  /** Options disponibles. */
  @Input() options: SelectOption[] = [];

  /** Texte affiché quand rien n'est sélectionné. */
  @Input() placeholder = 'Sélectionner...';

  /** Texte d'aide. */
  @Input() hint = '';

  /** Message d'erreur. */
  @Input() error = '';

  /** Désactive le champ. */
  @Input() disabled = false;

  protected readonly open = signal(false);
  protected values: string[] = [];

  private onChange: (value: string[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  private readonly elementRef = inject(ElementRef);

  writeValue(value: unknown): void {
    this.values = Array.isArray(value) ? value : [];
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  protected get summary(): string {
    if (this.values.length === 0) {
      return '';
    }
    const labels = this.values
      .map((value) => this.options.find((option) => option.value === value)?.label)
      .filter((label): label is string => Boolean(label));
    if (labels.length === 0) {
      return `${this.values.length} sélectionné(s)`;
    }
    return labels.join(', ');
  }

  protected toggleDropdown(): void {
    this.open.update((value) => !value);
  }

  protected isChecked(value: string): boolean {
    return this.values.includes(value);
  }

  protected toggleOption(option: SelectOption): void {
    if (this.isChecked(option.value)) {
      this.values = this.values.filter((value) => value !== option.value);
    } else {
      this.values = [...this.values, option.value];
    }
    this.onChange(this.values);
  }

  protected close(): void {
    this.open.set(false);
    this.onTouched();
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    const host = this.elementRef.nativeElement as HTMLElement;
    if (!host.contains(event.target as Node)) {
      this.open.set(false);
    }
  }
}
